import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  GasPump,
  Plus,
  Trash,
  CircleNotch,
  CheckCircle,
  Clock,
  PencilSimple,
  ArrowRight,
  X,
} from "@phosphor-icons/react";
import Pagination from "../shared/Pagination";
import { WlasnoscBadge } from "../shared/WlasnoscFilter";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { payoutDateFromDraft } from "../../utils/payoutDate";
import { vehicleLabel } from "../../utils/vehicleLabel";

const API = `${window.location.origin}/api`;

const MONTHS_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

function getTargetOptions(refDate) {
  // refDate = YYYY-MM-DD; returns 6 options (prev/curr/next × 01-15/16-30)
  if (!refDate) return [];
  const [y, m] = refDate.split("-").map(Number);
  const prevMonth = m === 1 ? 12 : m - 1;
  const prevYear = m === 1 ? y - 1 : y;
  const nextMonth = m === 12 ? 1 : m + 1;
  const nextYear = m === 12 ? y + 1 : y;
  return [
    { year: prevYear, month: prevMonth, period: "01-15", label: `${MONTHS_PL[prevMonth - 1]} ${prevYear} — 01-15` },
    { year: prevYear, month: prevMonth, period: "16-30", label: `${MONTHS_PL[prevMonth - 1]} ${prevYear} — 16-30` },
    { year: y, month: m, period: "01-15", label: `${MONTHS_PL[m - 1]} ${y} — 01-15` },
    { year: y, month: m, period: "16-30", label: `${MONTHS_PL[m - 1]} ${y} — 16-30` },
    { year: nextYear, month: nextMonth, period: "01-15", label: `${MONTHS_PL[nextMonth - 1]} ${nextYear} — 01-15` },
    { year: nextYear, month: nextMonth, period: "16-30", label: `${MONTHS_PL[nextMonth - 1]} ${nextYear} — 16-30` },
  ];
}

function fmtPLN(v) {
  return Number(v || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function EmployeePaliwoTab() {
  const [drafts, setDrafts] = useState([]);
  const [unassigned, setUnassigned] = useState([]);   // Orlen tankowania for my vehicles that need "Przepisz"
  const [przepiszTx, setPrzepiszTx] = useState(null); // tankowanie row selected for Przepisz
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ data: today, kwota: "" });
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  const targetOptions = getTargetOptions(form.data);
  const [targetIdx, setTargetIdx] = useState(4); // default: next month 01-15
  const [editingDraft, setEditingDraft] = useState(null); // draft object being edited
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const paginatedDrafts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return drafts.slice(start, start + pageSize);
  }, [drafts, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [drafts.length, pageSize]);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/employee-paliwo`);
      setDrafts(data || []);
    } catch (err) {
      console.error("Error fetching paliwo:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnassigned = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employee-paliwo/unassigned-tankowania`);
      setUnassigned(data || []);
    } catch (err) {
      // Silent fail: employees without vehicles get 200 [] anyway
      console.warn("unassigned-tankowania:", err.response?.status);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
    fetchUnassigned();
  }, [fetchDrafts, fetchUnassigned]);

  // Keep the paliwo tab in sync when admin adds new Orlen data / matches drafts.
  useAutoRefresh(() => {
    fetchDrafts();
    fetchUnassigned();
  });

  const startSave = (e) => {
    e?.preventDefault();
    if (!form.data || !form.kwota || Number(form.kwota) <= 0) {
      alert("Podaj datę i kwotę (>0).");
      return;
    }
    setEditingDraft(null);
    // Reset target to "next month 01-15" relative to entered date
    setTargetIdx(4);
    setShowTargetPicker(true);
  };

  const startEdit = (draft) => {
    setEditingDraft(draft);
    setForm({ data: draft.data, kwota: String(draft.kwota) });
    // Pre-select the matching target option (if any), else default 4
    const opts = getTargetOptions(draft.data);
    const idx = opts.findIndex(
      (o) =>
        o.year === draft.target_year &&
        o.month === draft.target_month &&
        o.period === draft.target_period
    );
    setTargetIdx(idx >= 0 ? idx : 4);
    setShowTargetPicker(true);
  };

  const confirmSave = async () => {
    const target = targetOptions[targetIdx];
    if (!target) {
      alert("Nieprawidłowy okres docelowy.");
      return;
    }
    setSaving(true);
    try {
      if (editingDraft) {
        await axios.put(`${API}/employee-paliwo/${editingDraft.id}`, {
          data: form.data,
          kwota: parseFloat(form.kwota),
          target_year: target.year,
          target_month: target.month,
          target_period: target.period,
        });
      } else {
        await axios.post(`${API}/employee-paliwo`, {
          data: form.data,
          kwota: parseFloat(form.kwota),
          target_year: target.year,
          target_month: target.month,
          target_period: target.period,
        });
      }
      setForm({ data: today, kwota: "" });
      setEditingDraft(null);
      setShowTargetPicker(false);
      await fetchDrafts();
    } catch (err) {
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const cancelTargetPicker = () => {
    if (saving) return;
    setShowTargetPicker(false);
    if (editingDraft) {
      // Reset the form back to clean state when cancelling edit
      setForm({ data: today, kwota: "" });
      setEditingDraft(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunąć ten wpis paliwa?")) return;
    try {
      await axios.delete(`${API}/employee-paliwo/${id}`);
      await fetchDrafts();
    } catch (err) {
      alert("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="employee-paliwo-tab">
      {/* Info banner — Orlen syncs automatically now */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 text-sm text-blue-900">
        <b>Automatyczna synchronizacja</b> — tankowania z karty Orlen pojawiają się tu same,
        dodane przez system. Formularz poniżej jest dostępny tylko dla wpisów spoza Orlenu
        (np. gotówka, inna stacja).
      </div>

      {/* "Do przypisania z Orlen" — one-click Przepisz for unlinked tankowania */}
      {unassigned.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="przepisz-section">
          <div className="flex items-start justify-between mb-3 gap-2">
            <div>
              <div className="flex items-center gap-2">
                <GasPump className="w-5 h-5 text-amber-700" weight="duotone" />
                <h3 className="font-semibold text-amber-900">Do przypisania z Orlen</h3>
                <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-semibold">
                  {unassigned.length}
                </span>
              </div>
              <p className="text-xs text-amber-800 mt-1">
                Tankowania z Twojej karty, których jeszcze nie przypisałeś do wypłaty. Kliknij <b>Przepisz</b>, żeby wybrać okres — kwota i data zaciągną się automatycznie.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {unassigned.slice(0, 8).map((tx) => (
              <div
                key={tx.id}
                className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2"
                data-testid={`unassigned-row-${tx.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-slate-800 tabular-nums">
                      {(tx.data || "").split("-").reverse().join(".")}
                    </span>
                    <span className="text-slate-500">·</span>
                    <span className="font-bold text-slate-900 tabular-nums">
                      {fmtPLN(tx.kwota)} zł
                    </span>
                    {tx.wlasnosc && <WlasnoscBadge value={tx.wlasnosc} size="xs" />}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {vehicleLabel(tx)}
                    {tx.stacja ? ` · ${tx.stacja}` : ""}
                    {tx.produkt ? ` · ${tx.produkt}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => setPrzepiszTx(tx)}
                  className="btn-primary shrink-0 flex items-center gap-1.5 text-sm py-1.5 px-3"
                  data-testid={`przepisz-btn-${tx.id}`}
                >
                  Przepisz <ArrowRight className="w-4 h-4" weight="bold" />
                </button>
              </div>
            ))}
            {unassigned.length > 8 && (
              <div className="text-xs text-amber-800 text-center pt-1">
                + {unassigned.length - 8} więcej…
              </div>
            )}
          </div>
        </div>
      )}

      {/* Przepisz modal — pick period only, data + kwota come from tankowanie */}
      {przepiszTx && (
        <PrzepiszModal
          tx={przepiszTx}
          onClose={() => setPrzepiszTx(null)}
          onSuccess={() => {
            setPrzepiszTx(null);
            fetchDrafts();
            fetchUnassigned();
          }}
        />
      )}

      {/* Add form (kept for non-Orlen edge cases) */}
      <details className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <summary className="p-4 sm:p-6 cursor-pointer flex items-center gap-2 select-none">
          <GasPump className="w-5 h-5 text-emerald-600" weight="duotone" />
          <h3 className="font-semibold text-slate-800">Nowy wpis paliwa (ręcznie)</h3>
        </summary>
        <div className="px-4 sm:px-6 pb-6">
        <form
          onSubmit={startSave}
          className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
        >
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Data tankowania
            </label>
            <input
              type="date"
              value={form.data}
              onChange={(e) => setForm({ ...form, data: e.target.value })}
              className="input-field w-full"
              required
              data-testid="paliwo-date-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
              Kwota (zł)
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.kwota}
              onChange={(e) => setForm({ ...form, kwota: e.target.value })}
              className="input-field w-full"
              required
              placeholder="np. 300.00"
              data-testid="paliwo-kwota-input"
            />
          </div>
          <button
            type="submit"
            className="btn-primary flex items-center justify-center gap-2"
            data-testid="paliwo-save-btn"
          >
            <Plus className="w-4 h-4" /> Dodaj
          </button>
        </form>
        </div>
      </details>

      {/* Target picker dialog */}
      {showTargetPicker && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          data-testid="paliwo-target-modal"
          onClick={cancelTargetPicker}
        >
          <div
            className="bg-white rounded-xl border border-border max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg text-slate-800 mb-2">
              {editingDraft ? "Edytuj wpis paliwa" : "Do której wypłaty dodać?"}
            </h3>
            {editingDraft && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
                    Data tankowania
                  </label>
                  <input
                    type="date"
                    value={form.data}
                    onChange={(e) => setForm({ ...form, data: e.target.value })}
                    className="input-field w-full"
                    data-testid="paliwo-edit-date-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
                    Kwota (zł)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.kwota}
                    onChange={(e) => setForm({ ...form, kwota: e.target.value })}
                    className="input-field w-full"
                    data-testid="paliwo-edit-kwota-input"
                  />
                </div>
              </div>
            )}
            <p className="text-sm text-slate-600 mb-4">
              Wpis: <b>{form.data}</b> — <b>{fmtPLN(form.kwota)} zł</b>.
              Wybierz okres wypłaty, do którego administrator automatycznie
              doliczy tę kwotę jako potrącenie za paliwo (poprzedni / bieżący /
              następny miesiąc).
            </p>
            <div className="space-y-2">
              {targetOptions.map((opt, idx) => (
                <label
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    targetIdx === idx
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`paliwo-target-option-${idx}`}
                >
                  <input
                    type="radio"
                    name="target"
                    checked={targetIdx === idx}
                    onChange={() => setTargetIdx(idx)}
                    className="w-4 h-4"
                  />
                  <span className="font-medium text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={cancelTargetPicker}
                disabled={saving}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Anuluj
              </button>
              <button
                onClick={confirmSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="paliwo-confirm-target-btn"
              >
                {saving ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" /> Zapisuję...
                  </>
                ) : (
                  editingDraft ? "Zapisz zmiany" : "Zapisz"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 text-sm sm:text-base">
            Moje wpisy paliwa ({drafts.length})
          </h3>
        </div>
        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <CircleNotch className="w-6 h-6 animate-spin" />
          </div>
        ) : drafts.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-sm">
            Brak wpisów. Dodaj pierwszy powyżej.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-px">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-3 sm:px-4 text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">Data</th>
                <th className="text-right py-3 px-3 sm:px-4 text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">Kwota</th>
                <th className="text-center py-3 px-3 sm:px-4 text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap" title="Opłacone — administrator dopisał tę kwotę do wypłaty">Opłac.</th>
                <th className="text-left py-3 px-3 sm:px-4 text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">Wypłata</th>
                <th className="text-center py-3 px-3 sm:px-4 text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">Status</th>
                <th className="text-right py-3 px-3 sm:px-4 text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDrafts.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`paliwo-row-${d.id}`}
                >
                  <td className="py-3 px-3 sm:px-4 font-medium text-slate-800 whitespace-nowrap">{d.data}</td>
                  <td className="py-3 px-3 sm:px-4 text-right tabular-nums font-bold text-slate-800 whitespace-nowrap">
                    {fmtPLN(d.kwota)} zł
                  </td>
                  <td
                    className="py-3 px-3 sm:px-4 text-center"
                    data-testid={`paliwo-paid-${d.id}`}
                    title={d.applied ? "Dopisano do wypłaty przez administratora" : "Jeszcze nie dopisano"}
                  >
                    {d.applied ? (
                      <CheckCircle
                        className="w-5 h-5 text-emerald-600 inline-block"
                        weight="fill"
                      />
                    ) : (
                      <span className="text-slate-300 text-xl">○</span>
                    )}
                  </td>
                  <td className="py-3 px-3 sm:px-4 text-sm text-slate-600 whitespace-nowrap">
                    <div>
                      {MONTHS_PL[(d.target_month || 1) - 1]} {d.target_year} — {d.target_period}
                    </div>
                    {payoutDateFromDraft(d) && (
                      <div
                        className="text-[10px] text-slate-500 mt-0.5"
                        title="Faktyczna wypłata (2 tygodnie po zakończeniu okresu)"
                      >
                        wypłata: <span className="font-semibold text-slate-700">{payoutDateFromDraft(d)}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-3 sm:px-4 text-center">
                    {d.applied ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        <CheckCircle className="w-3.5 h-3.5" weight="fill" />
                        Zastosowano
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        <Clock className="w-3.5 h-3.5" weight="fill" />
                        Oczekuje
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 sm:px-4 text-right whitespace-nowrap">
                    {!d.applied && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => startEdit(d)}
                          className="p-2 hover:bg-muted text-foreground rounded-lg"
                          data-testid={`paliwo-edit-${d.id}`}
                          title="Edytuj"
                        >
                          <PencilSimple className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(d.id)}
                          className="p-2 hover:bg-red-50 text-red-600 rounded-lg"
                          data-testid={`paliwo-delete-${d.id}`}
                          title="Usuń"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!loading && drafts.length > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={drafts.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testId="employee-paliwo-pagination"
          />
        )}
      </div>
    </div>
  );
}


// One-click Przepisz dialog — only asks for target period.
// Date + amount come from the source tankowanie row.
function PrzepiszModal({ tx, onClose, onSuccess }) {
  const targetOptions = useMemo(() => getTargetOptions(tx.data), [tx.data]);
  const initialIdx = useMemo(() => {
    // Suggest current-period option: if day <= 15 → same month 01-15 else 16-30
    try {
      const day = parseInt((tx.data || "").split("-")[2], 10) || 1;
      return day <= 15 ? 2 : 3; // indices in getTargetOptions layout
    } catch {
      return 2;
    }
  }, [tx.data]);
  const [idx, setIdx] = useState(initialIdx);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    const opt = targetOptions[idx];
    if (!opt) return;
    setSaving(true);
    setErr(null);
    try {
      await axios.post(`${API}/employee-paliwo/from-tankowanie`, {
        tankowanie_id: tx.id,
        target_year: opt.year,
        target_month: opt.month,
        target_period: opt.period,
      });
      onSuccess();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      data-testid="przepisz-modal-overlay"
    >
      <div
        className="bg-white rounded-xl border border-border max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="przepisz-modal"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="font-bold text-lg flex items-center gap-2 text-slate-800">
            <GasPump className="w-5 h-5 text-emerald-600" weight="duotone" />
            Przepisz do wypłaty
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="przepisz-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-1">Tankowanie</div>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 tabular-nums">
                  {(tx.data || "").split("-").reverse().join(".")}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {vehicleLabel(tx)}{tx.stacja ? ` · ${tx.stacja}` : ""}
                </div>
              </div>
              <div className="text-xl font-bold text-slate-900 tabular-nums whitespace-nowrap">
                {fmtPLN(tx.kwota)} zł
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">
              Okres wypłaty
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {targetOptions.map((o, i) => (
                <button
                  key={`${o.year}-${o.month}-${o.period}`}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`text-left text-sm px-3 py-2 rounded-md border transition-colors ${
                    idx === i
                      ? "border-emerald-500 bg-emerald-50 text-emerald-900 font-semibold"
                      : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                  }`}
                  data-testid={`przepisz-target-${i}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">{err}</div>
          )}
        </div>
        <div className="flex gap-2 border-t border-slate-200 px-5 py-4">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={saving}>
            Anuluj
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
            data-testid="przepisz-submit"
          >
            {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" weight="bold" />}
            {saving ? "Zapisywanie..." : "Przepisz"}
          </button>
        </div>
      </div>
    </div>
  );
}
